// ExtractionIssueMarker - a leading alert-triangle on a file row whose extraction
// failed outright or lost content while completing (content_warnings), so the
// operator can spot problem files without expanding every row. Renders nothing for
// a clean row. Red for a failure, amber for an incomplete read.
import { Tooltip } from "./Tooltip";
import { useUploadExtracted } from "../api";
import type { ContentWarning } from "../api/hooks/useUploadExtracted";

interface Props {
  uploadId: string;
}

const issueLines = (
  status: string,
  error: string | null,
  contentWarnings: ContentWarning[],
): string[] => {
  const lines: string[] = [];
  if (status === "failed") {
    lines.push(error ? `Extraction failed: ${error}` : "Extraction failed.");
  }
  for (const w of contentWarnings) lines.push(`Incomplete read: ${w.detail}`);
  return lines;
};

export const ExtractionIssueMarker = ({
  uploadId,
}: Props): JSX.Element | null => {
  const { data } = useUploadExtracted(uploadId);
  if (!data) return null;

  const lines = issueLines(
    data.extraction_status,
    data.extraction_error,
    data.content_warnings ?? [],
  );
  if (lines.length === 0) return null;

  // Failed extraction is louder than an incomplete read.
  const isFailed = data.extraction_status === "failed";
  const color = isFailed ? "#b91c1c" : "#b45309";
  // Keep the issue detail in aria-label so screen-reader users reach it
  // without firing the hover-only Tooltip - mirroring the old <span title=...>
  // shape (the native title attribute used to surface the lines verbatim).
  // The visible Tooltip bubble renders the same lines as <div>s so multi-issue
  // tooltips keep their line breaks (the bubble's CSS is white-space: normal).
  const labelPrefix = isFailed ? "Extraction failed" : "Extraction issue";
  const ariaLabel = `${labelPrefix}: ${lines.join("; ")}`;

  return (
    <Tooltip
      content={
        <>
          {lines.map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </>
      }
    >
      <span
        role="img"
        aria-label={ariaLabel}
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginRight: 6,
          color,
          lineHeight: 1,
          cursor: "help",
          flexShrink: 0,
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ display: "block" }}
        >
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </span>
    </Tooltip>
  );
};
